import React, { useCallback, useState } from "react";
import { SendIcon } from "./TriloChatIcons";
import {
  CHAT_MESSAGES,
  CHAT_TEXTAREA_MAX_HEIGHT_PX,
  CHAT_TEXTAREA_MIN_HEIGHT_PX,
} from "../utils/triloChatConstants";
import { NO_PERMISSION_TITLE } from "../utils/permissions";

/**
 * TriloChatComposer
 *
 * Message input for MW: the quick-reply buttons offered by the last reply, the
 * auto-growing textarea and the send button.
 *
 * Props:
 * - quickReplies: Suggested replies from the last assistant message
 * - onSend: (text: string) => void
 * - isStreaming: Whether a reply is in flight (input is locked meanwhile)
 * - disabled: Whether the user may not send messages
 * - disabledTitle: Why it is disabled, shown on hover while disabled
 * - textareaRef: Ref the page uses to focus the input after starting a new chat
 */
const TriloChatComposer = ({
  quickReplies,
  onSend,
  isStreaming,
  disabled,
  disabledTitle = NO_PERMISSION_TITLE,
  textareaRef,
}) => {
  const [input, setInput] = useState("");

  const resetHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = `${CHAT_TEXTAREA_MIN_HEIGHT_PX}px`;
    }
  }, [textareaRef]);

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    resetHeight();
    onSend(text);
  }, [input, isStreaming, onSend, resetHeight]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit]
  );

  const handleChange = useCallback(e => {
    setInput(e.target.value);
    // Auto-grow the textarea between the min/max heights set in chat.scss.
    const el = e.target;
    el.style.height = `${CHAT_TEXTAREA_MIN_HEIGHT_PX}px`;
    el.style.height =
      Math.min(el.scrollHeight, CHAT_TEXTAREA_MAX_HEIGHT_PX) + "px";
  }, []);

  return (
    <>
      {quickReplies.length > 0 && (
        <div className="trilochat__quick-replies">
          {quickReplies.map((opt, i) => (
            <button
              key={i}
              className="trilochat__quick-reply"
              onClick={() => onSend(opt)}
              disabled={disabled}
              title={disabled ? disabledTitle : undefined}>
              {opt}
            </button>
          ))}
        </div>
      )}

      <div className="trilochat__input-area">
        <div className="trilochat__input-row">
          <textarea
            ref={textareaRef}
            className="trilochat__textarea"
            rows={1}
            placeholder={CHAT_MESSAGES.composerPlaceholder}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isStreaming || disabled}
            title={disabled ? disabledTitle : undefined}
          />
          <button
            className="trilochat__send-btn"
            onClick={submit}
            disabled={!input.trim() || isStreaming || disabled}
            aria-label="Send message">
            <SendIcon />
          </button>
        </div>
        <p className="trilochat__hint">{CHAT_MESSAGES.composerHint}</p>
      </div>
    </>
  );
};

export default TriloChatComposer;
